import React, { useState, useRef } from "react";
import { useFieldArray, useFormContext, get } from "react-hook-form";
import StepHeader from "../StepHeader";
import RHFInput from "@/components/form/RHFInput";
import RHFSelect from "@/components/form/RHFSelect";
import { ANSWER_TYPE } from "@/config/dropdownData";
import { Button } from "@/components/ui/Button";
import { Plus, X, ChevronDown, ChevronUp } from "lucide-react";

const ITEMS_MAX = 3;

// ── Section card ──
const SectionCard = ({ label, open, onToggle, onRemove, children, disabled = false }) => (
  <>
    <div className="rounded-xl md:px-5 px-2 py-1 space-y-5">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <span className="block text-md font-medium text-text-primary truncate">{label}</span>
        </div>
        <div className="flex items-center gap-1 py-2 flex-shrink-0">
          <Button type="button" onClick={onToggle} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-tertiary transition">
            {open ? <ChevronUp size={16} className="text-text-primary" /> : <ChevronDown size={16} className="text-text-primary" />}
          </Button>
          <Button
            type="button"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-500/40 hover:bg-red-500/20 hover:text-red-400 text-white transition p-0"
          >
            <X size={14} />
          </Button>
        </div>
      </div>
      {open && <div className="space-y-4">{children}</div>}
    </div>
    <hr className="border-border-primary" />
  </>
);

// ── Single entry card ──
const EntryCard = ({ sectionIndex, entryIndex, entry, onRemove, onFieldChange, onValueChange, disabled }) => {
  const fields = entry.length > 0 ? entry : [{ key: "", value: "" }];

  return (
    <div className="relative border border-border-primary rounded-xl p-4 space-y-3 bg-background-secondary/40">
      {/* Remove entry */}
      {onRemove && (
        <Button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          className="absolute -top-2.5 -right-2.5 w-5 h-5 flex items-center justify-center rounded-full text-text-secondary hover:text-red-400 bg-background-tertiary hover:bg-red-900/60 transition z-10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <X size={11} />
        </Button>
      )}

      {/* Key-value rows. Grid, not flex + fixed-width spacers: a grid template
          shared with the header row below is what keeps "Field"/"Value"
          sitting above their inputs regardless of label length. */}
      {fields.map((field, f) => (
        <div key={f} className="grid grid-cols-[2fr_auto_3fr_auto] items-start gap-2">
          {/* Key input */}
          <RHFInput
            name={`additionalSections.${sectionIndex}.items.${entryIndex}.${f}.key`}
            placeholder="Field name"
            inputClass="text-xs px-3 py-2"
            disabled={disabled}
          />

          {/* Divider */}
          <span className="text-text-primary text-xs pt-2.5">:</span>

          {/* Value input */}
          <RHFInput
            name={`additionalSections.${sectionIndex}.items.${entryIndex}.${f}.value`}
            placeholder={`Enter ${field.key?.replace(/_/g, " ") || "value"}…`}
            inputClass="text-xs px-3 py-2"
            onChange={onValueChange}
            disabled={disabled}
          />

          {/* Remove field pair */}
          <Button
            type="button"
            onClick={() => onFieldChange(entryIndex, f, "__remove__", null)}
            disabled={disabled}
            className="mt-1.5 w-5 h-5 flex items-center justify-center rounded-full text-text-secondary hover:text-red-400 hover:bg-red-900/40 transition flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={10} />
          </Button>
        </div>
      ))}

      {/* Add field pair inside entry */}
      <Button
        type="button"
        onClick={() => onFieldChange(entryIndex, null, "__add_field__", null)}
        disabled={disabled}
        className="flex items-center gap-1 text-xs text-text-secondary hover:text-text-secondary/90 transition mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Plus size={11} /> Add field
      </Button>
    </div>
  );
};

// The two values a Yes/No answer may carry — what the server normalises every
// answer to, not the question's answer_type.
const YES_NO_OPTIONS = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

// Keep in sync with MAX_SHORT_TEXT_ANSWER in the backend service
// (server/app/api/v2/application_questions/service.py).
const MAX_SHORT_TEXT_ANSWER = 500;

const StepMoreInfo = ({ showSocials = true, mandatoryFields = [], disabled = false, screeningQuestions = [] }) => {
  const { control, formState, getFieldState, getValues, setError, clearErrors } = useFormContext();
  const entryRefs = useRef({});

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "additionalSections",
  });

  const [expandedSections, setExpandedSections] = useState({});
  const [openCards, setOpenCards] = useState({});
  const lastSectionRef = useRef(null);

  const toggleSection = (index) => setExpandedSections((prev) => ({ ...prev, [index]: !prev[index] }));

  const toggleCard = (index) => setOpenCards((prev) => ({ ...prev, [index]: !prev[index] }));

  const isCardOpen = (index) => openCards[index] !== false;

  // ── Mutate a field's key or value, or remove/add a field pair ──
  const handleFieldChange = (sectionIndex, entryIndex, fieldIndex, type, newValue) => {
    const n = getValues("additionalSections");
    const entry = [...(n[sectionIndex].items[entryIndex] ?? [])];

    if (type === "__remove__") {
      if (entry.length <= 1) return;
      entry.splice(fieldIndex, 1);
    } else if (type === "__add_field__") {
      entry.push({ key: "", value: "" });
    } else {
      entry[fieldIndex] = { ...entry[fieldIndex], [type]: newValue };
    }

    n[sectionIndex].items[entryIndex] = entry;
    replace(n);
  };

  const canAddSection = () => {
    if (fields.length === 0) return true;

    const values = getValues("additionalSections");
    const last = values[values.length - 1];
    const lastIndex = values.length - 1;

    const titleFilled = last?.section_title?.trim() !== "";
    const itemFilled = last?.items?.some((entry) => Array.isArray(entry) && entry.some((f) => f?.value?.trim() !== ""));

    // ← Check for duplicate title across all sections
    const isDuplicate = titleFilled && values.slice(0, -1).some((s) => s.section_title?.trim().toLowerCase() === last.section_title.trim().toLowerCase());

    if (!titleFilled) {
      setError(`additionalSections.${lastIndex}.section_title`, {
        type: "manual",
        message: "Please fill the section title before adding a new section.",
      });
    } else if (isDuplicate) {
      setError(`additionalSections.${lastIndex}.section_title`, {
        type: "manual",
        message: "A section with this title already exists. Add entries to the existing section instead.",
      });
    } else {
      clearErrors(`additionalSections.${lastIndex}.section_title`);
    }

    if (!itemFilled) {
      setError(`additionalSections.${lastIndex}.items`, {
        type: "manual",
        message: "Please add at least one item before adding a new section.",
      });
    } else {
      clearErrors(`additionalSections.${lastIndex}.items`);
    }

    if (!titleFilled || isDuplicate || !itemFilled) {
      setTimeout(() => {
        lastSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return false;
    }

    return true;
  };

  const handleAddSection = () => {
    if (canAddSection()) {
      setOpenCards((prev) => {
        const next = { ...prev };
        fields.forEach((_, i) => {
          next[i] = false;
        });
        next[fields.length] = true;
        return next;
      });
      append({ section_title: "", items: [[{ key: "", value: "" }]] });
    }
  };

  const handleAddItem = (sectionIndex) => {
    const n = getValues("additionalSections");
    const currentItems = n[sectionIndex].items || [];
    const lastEntry = currentItems[currentItems.length - 1] ?? [];
    const newIndex = currentItems.length;

    const lastEmpty = lastEntry.length > 0 && lastEntry.every((f) => f?.value?.trim() === "");
    if (lastEmpty && currentItems.length > 0) return;

    const newEntry = [{ key: "", value: "" }];

    n[sectionIndex].items = [...currentItems, newEntry];
    replace(n);
    setExpandedSections((prev) => ({ ...prev, [sectionIndex]: true }));

    requestAnimationFrame(() => {
      setTimeout(() => {
        entryRefs.current[`${sectionIndex}-${newIndex}`]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 50);
    });
  };

  return (
    <div>
      <StepHeader title="Additional Information" onAdd={handleAddSection} disabled={disabled} />

      {/* Screening questions the recruiter configured on this job. Rendered
          from the job's active questions; answers bind to `screening_answers`
          and are sent with the application on submit. Required questions show a
          * and are gated on submit (server-enforced too). */}
      {Array.isArray(screeningQuestions) && screeningQuestions.length > 0 && (
        <div className="md:px-5 mb-8 space-y-4">
          <h3 className="text-sm font-medium text-text-secondary">Screening Questions</h3>
          {screeningQuestions.map((q) => (
            <div key={q.id}>
              {q.answer_type === ANSWER_TYPE.YES_NO ? (
                <RHFSelect
                  name={`screening_answers.${q.id}`}
                  label={q.question}
                  required={!!q.is_required}
                  placeholder="Select"
                  options={YES_NO_OPTIONS}
                  disabled={disabled}
                />
              ) : (
                <RHFInput
                  name={`screening_answers.${q.id}`}
                  label={q.question}
                  required={!!q.is_required}
                  placeholder={`Your answer (max ${MAX_SHORT_TEXT_ANSWER} characters)`}
                  maxLength={MAX_SHORT_TEXT_ANSWER}
                  maxLengthMessage={`Answer must be ${MAX_SHORT_TEXT_ANSWER} characters or fewer`}
                  disabled={disabled}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-8 md:px-5">
        <div>
          {fields.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-4">Add domain-specific sections like Awards, Certifications, Interests, etc.</p>
          ) : (
            <div className="space-y-5">
              {fields.map((section, i) => {
                const open = isCardOpen(i);
                const isExpanded = !!expandedSections[i];
                const items = section.items || [];
                const visibleItems = isExpanded ? items : items.slice(0, ITEMS_MAX);
                const hiddenCount = items.length - ITEMS_MAX;
                const { error: itemsError } = getFieldState(`additionalSections.${i}.items`, formState);

                const rawLabel = getValues(`additionalSections.${i}.section_title`) || "";
                const cardLabel = rawLabel.trim() || `Section ${i + 1}`;
                return (
                  <div key={section.id} ref={i === fields.length - 1 ? lastSectionRef : null}>
                    <SectionCard disabled={disabled} label={cardLabel} open={open} onToggle={() => toggleCard(i)} onRemove={() => remove(i)}>
                      <RHFInput
                        name={`additionalSections.${i}.section_title`}
                        label="Section Title"
                        placeholder="e.g. Awards, Certifications, Interests…"
                        disabled={disabled}
                      />

                      {/* Column headers. Same grid template and horizontal
                          inset as EntryCard's `p-4` below, so "Field"/"Value"
                          sit directly above the inputs they label instead of
                          drifting with label length. */}
                      {visibleItems.length > 0 && (
                        <div className="grid grid-cols-[2fr_auto_3fr_auto] items-center gap-2 px-4">
                          <span className="text-xs text-text-secondary font-medium">Field</span>
                          <span />
                          <span className="text-xs text-text-secondary font-medium">Value</span>
                          <span className="w-5" />
                        </div>
                      )}

                      {/* Entries */}
                      <div className="space-y-3">
                        {visibleItems.map((entry, a) => (
                          <div
                            key={a}
                            ref={(el) => {
                              entryRefs.current[`${i}-${a}`] = el;
                            }}
                          >
                            <EntryCard
                              key={a}
                              sectionIndex={i}
                              entryIndex={a}
                              entry={Array.isArray(entry) ? entry : []}
                              onRemove={
                                items.length > 1
                                  ? () => {
                                      const nextRef = entryRefs.current[`${i}-${a + 1}`] || entryRefs.current[`${i}-${a - 1}`];

                                      const n = getValues("additionalSections");
                                      n[i].items.splice(a, 1);
                                      replace(n);

                                      setTimeout(() => {
                                        nextRef?.scrollIntoView({
                                          behavior: "instant",
                                          block: "nearest",
                                        });
                                      }, 0);
                                    }
                                  : null
                              }
                              onFieldChange={(entryIndex, fieldIndex, type, value) => handleFieldChange(i, entryIndex, fieldIndex, type, value)}
                              onValueChange={(event) => {
                                if (event.target.value.trim()) clearErrors(`additionalSections.${i}.items`);
                              }}
                              disabled={disabled}
                            />
                          </div>
                        ))}
                      </div>
                      {itemsError?.message && <p className="text-red-500 text-xs">{itemsError.message}</p>}

                      {/* Section actions */}
                      <div className="flex gap-2 justify-end px-1">
                        {!isExpanded && hiddenCount > 0 && (
                          <Button
                            type="button"
                            onClick={() => toggleSection(i)}
                            className="flex items-center justify-center gap-1.5 py-2 px-4 rounded-lg text-xs text-text-secondary hover:text-text-primary bg-background-secondary hover:bg-background-secondary/80 border border-background-tertiary border-dashed transition"
                          >
                            <Plus size={12} /> {hiddenCount} more
                          </Button>
                        )}
                        {isExpanded && items.length > ITEMS_MAX && (
                          <Button
                            type="button"
                            onClick={() => toggleSection(i)}
                            className="flex items-center justify-center gap-1.5 py-2 px-4 rounded-lg text-xs text-text-secondary hover:text-text-primary bg-background-secondary hover:bg-background-secondary/80 border border-background-primary border-dashed transition"
                          >
                            Show less
                          </Button>
                        )}
                        <Button
                          type="button"
                          onClick={() => handleAddItem(i)}
                          disabled={disabled}
                          className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary bg-background-secondary hover:bg-background-secondary/80 border border-background-primary border-dashed transition px-4 py-2 rounded-lg"
                        >
                          <Plus size={13} /> Add Entry
                        </Button>
                      </div>
                    </SectionCard>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Social Media */}
        {showSocials && (
          <div className="px-2">
            <h3 className="text-sm font-medium text-text-secondary mb-4">
              Social Media <span className="font-normal">(Optional)</span>
            </h3>
            <div className="space-y-4">
              <RHFInput name="social.instagram_url" label="Instagram" placeholder="https://instagram.com/yourhandle" disabled={disabled} />
              <RHFInput name="social.facebook_url" label="Facebook" placeholder="https://facebook.com/yourprofile" disabled={disabled} />
              <RHFInput name="social.twitter_url" label="Twitter" placeholder="https://twitter.com/yourhandle" disabled={disabled} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepMoreInfo;
